import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion, type Variants } from 'framer-motion';
import {
  getScannerPrefs,
  upsertScannerPrefs,
  getCompatPrefs,
  upsertCompatPrefs,
  COMPAT_ALLERGENS,
  type PrefsOut,
  type CompatPrefsOut,
} from '../api/endpoints';
import { useUser } from '../contexts/UserContext';
import ModalShell from '../components/ModalShell';
import type { ScreenProps } from '../types/screen';
import { getErrorMessage } from '../utils/errors';

export interface PreferencesInput {
  /** Which preference set to focus when the screen opens. */
  initialTab?: 'scanner' | 'compat';
}

export interface PreferencesResult {
  scanner?: PrefsOut;
  compat?: CompatPrefsOut;
}

const SCANNER_ALLERGENS = [
  'nickel', 'benzoic_acid', 'methylisothiazolinone', 'methylchloroisothiazolinone',
  'parabens', 'lanolin', 'fragrance', 'linalool', 'limonene', 'citral',
  'hydroxycitronellal', 'balsam_peru', 'tocopherol_allergy',
] as const;

const SENS_OPTIONS = [
  { v: 'none', label: 'None' },
  { v: 'possible', label: 'Possible' },
  { v: 'likely', label: 'Likely' },
] as const;

const ETHICAL_OPTIONS = [
  { v: 'off', label: 'Off' },
  { v: 'vegan', label: 'Vegan' },
  { v: 'halal', label: 'Halal' },
  { v: 'cruelty_free', label: 'Cruelty-free' },
] as const;

const labelize = (k: string) => k.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

function Toggle({
  label, hint, checked, onChange,
}: { label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div
      className="card card--flat card--bordered row-4"
      style={{ padding: 'var(--s-3) var(--s-4)' }}
    >
      <div className="min-w-0 flex-1">
        <div className="t-small" style={{ fontWeight: 500 }}>{label}</div>
        {hint && <div className="t-label t-mute" style={{ marginTop: 2 }}>{hint}</div>}
      </div>
      <button
        type="button"
        onClick={() => { onChange(!checked); }}
        className="relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent"
        style={{ background: checked ? 'var(--c-deep-teal)' : 'rgba(31,36,33,.15)', transition: 'background 0.18s ease' }}
        aria-pressed={checked}
      >
        <span
          className={[
            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition',
            checked ? 'translate-x-5' : 'translate-x-0',
          ].join(' ')}
        />
      </button>
    </div>
  );
}

type SkinTypePref = 'oily' | 'dry' | 'normal' | 'combination';

interface PrefsState {
  allergies: string[];
  sensitivity: 'none' | 'possible' | 'likely';
  acne_prone: boolean;
  ethical_mode: 'off' | 'vegan' | 'halal' | 'cruelty_free';
  cautious_actives: boolean;
  skin_type?: SkinTypePref | null;
}

const DEFAULT_PREFS: PrefsState = {
  allergies: [],
  sensitivity: 'likely',
  acne_prone: false,
  ethical_mode: 'off',
  cautious_actives: false,
  skin_type: null,
};

const SKIN_TYPE_OPTIONS: { v: SkinTypePref | null; label: string }[] = [
  { v: null, label: 'Auto (from scans)' },
  { v: 'oily', label: 'Oily' },
  { v: 'dry', label: 'Dry' },
  { v: 'normal', label: 'Normal' },
  { v: 'combination', label: 'Combination' },
];

function PrefsForm({
  state, setState, allergens, itemVariants, showSkinType,
}: {
  state: PrefsState;
  setState: React.Dispatch<React.SetStateAction<PrefsState>>;
  allergens: readonly string[];
  itemVariants: Variants;
  showSkinType?: boolean;
}) {
  const toggleAllergen = (a: string) =>
    { setState((s) => ({
      ...s,
      allergies: s.allergies.includes(a) ? s.allergies.filter((x) => x !== a) : [...s.allergies, a],
    })); };

  return (
    <div className="stack-6">
      <motion.section variants={itemVariants} className="stack-3">
        <div className="t-small" style={{ fontWeight: 600 }}>Ingredients to avoid</div>
        <p className="t-small t-mute">Pick ingredients you&apos;d rather avoid and we&apos;ll flag products that contain them.</p>
        <div className="flex flex-wrap gap-2">
          {allergens.map((a) => {
            const active = state.allergies.includes(a);
            return (
              <button
                key={a}
                type="button"
                onClick={() => { toggleAllergen(a); }}
                className={active ? 'pill pill--teal' : 'pill'}
                style={{ cursor: 'pointer' }}
              >
                {active && <span className="dot" />}
                {labelize(a)}
              </button>
            );
          })}
        </div>
      </motion.section>

      <motion.section variants={itemVariants} className="stack-3">
        <div className="t-small" style={{ fontWeight: 600 }}>Skin sensitivity</div>
        <div
          className="card card--flat card--bordered"
          style={{ padding: 0, overflow: 'hidden', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}
        >
          {SENS_OPTIONS.map((o, i) => (
            <button
              key={o.v}
              type="button"
              onClick={() => { setState((s) => ({ ...s, sensitivity: o.v })); }}
              className="t-small transition"
              style={{
                padding: 'var(--s-3)',
                textAlign: 'center',
                background: state.sensitivity === o.v ? 'var(--c-deep-teal)' : 'transparent',
                color: state.sensitivity === o.v ? 'var(--c-cream)' : 'var(--ink-muted)',
                borderRight: i < SENS_OPTIONS.length - 1 ? '1px solid var(--rule)' : 'none',
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      </motion.section>

      <motion.section variants={itemVariants} className="stack-3">
        <div className="t-small" style={{ fontWeight: 600 }}>Ethical preference</div>
        <div className="flex flex-wrap gap-2">
          {ETHICAL_OPTIONS.map((o) => {
            const active = state.ethical_mode === o.v;
            return (
              <button
                key={o.v}
                type="button"
                onClick={() => { setState((s) => ({ ...s, ethical_mode: o.v })); }}
                className={active ? 'pill pill--teal' : 'pill'}
                style={{ cursor: 'pointer' }}
              >
                {active && <span className="dot" />}
                {o.label}
              </button>
            );
          })}
        </div>
      </motion.section>

      {showSkinType && (
        <motion.section variants={itemVariants} className="stack-3">
          <div className="t-small" style={{ fontWeight: 600 }}>Skin type</div>
          <p className="t-small t-mute">Tailors your product analysis. Auto blends your recent skin scans.</p>
          <div className="flex flex-wrap gap-2">
            {SKIN_TYPE_OPTIONS.map((o) => {
              const active = (state.skin_type ?? null) === o.v;
              return (
                <button
                  key={String(o.v)}
                  type="button"
                  onClick={() => { setState((s) => ({ ...s, skin_type: o.v })); }}
                  className={active ? 'pill pill--teal' : 'pill'}
                  style={{ cursor: 'pointer' }}
                >
                  {active && <span className="dot" />}
                  {o.label}
                </button>
              );
            })}
          </div>
        </motion.section>
      )}

      <motion.section variants={itemVariants} className="grid sm:grid-cols-3 gap-3">
        <Toggle
          label="Breakout-prone"
          hint="Prefer non-comedogenic picks."
          checked={state.acne_prone}
          onChange={(v) => { setState((s) => ({ ...s, acne_prone: v })); }}
        />
        <Toggle
          label="Prefer gentle formulas"
          hint="Go easy on strong actives."
          checked={state.cautious_actives}
          onChange={(v) => { setState((s) => ({ ...s, cautious_actives: v })); }}
        />
      </motion.section>
    </div>
  );
}

const PreferencesScreen: React.FC<ScreenProps<PreferencesInput, PreferencesResult>> = ({
  mode,
  input,
  onComplete,
  onCancel,
}) => {
  const { refreshPrefs } = useUser();
  const [tab, setTab] = useState<'scanner' | 'compat'>(input?.initialTab ?? 'scanner');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [scanner, setScanner] = useState<PrefsState>(DEFAULT_PREFS);
  const [compat, setCompat] = useState<PrefsState>(DEFAULT_PREFS);
  const [scannerOriginal, setScannerOriginal] = useState<PrefsOut | null>(null);
  const [compatOriginal, setCompatOriginal] = useState<CompatPrefsOut | null>(null);

  const prefersReduced = useReducedMotion();
  const spring = prefersReduced
    ? { duration: 0 }
    : { type: 'spring' as const, stiffness: 380, damping: 34, mass: 0.95 };

  const pageIn: Variants = {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0, transition: spring },
  };
  const stagger: Variants = {
    hidden: {},
    visible: { transition: { staggerChildren: prefersReduced ? 0 : 0.06 } },
  };
  const item: Variants = {
    hidden: { opacity: 0, y: 6 },
    visible: { opacity: 1, y: 0, transition: spring },
  };

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setErr(null);
      try {
        const [s, c] = await Promise.allSettled([getScannerPrefs(), getCompatPrefs()]);
        if (s.status === 'fulfilled') {
          setScannerOriginal(s.value);
          setScanner({
            allergies: s.value.allergies,
            sensitivity: s.value.sensitivity,
            acne_prone: s.value.acne_prone,
            ethical_mode: s.value.ethical_mode,
            cautious_actives: s.value.cautious_actives,
            skin_type: s.value.skin_type ?? null,
          });
        }
        if (c.status === 'fulfilled') {
          setCompatOriginal(c.value);
          setCompat({
            allergies: c.value.allergies,
            sensitivity: c.value.sensitivity,
            acne_prone: c.value.acne_prone,
            ethical_mode: c.value.ethical_mode,
            cautious_actives: c.value.cautious_actives,
          });
        }
      } catch (error: unknown) {
        setErr(getErrorMessage(error, 'Failed to load preferences'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const dirty = useMemo(() => {
    const cmp = (a: PrefsState, b: PrefsState) => JSON.stringify(a) !== JSON.stringify(b);
    if (!scannerOriginal) return false;
    const sOrig: PrefsState = {
      allergies: scannerOriginal.allergies,
      sensitivity: scannerOriginal.sensitivity,
      acne_prone: scannerOriginal.acne_prone,
      ethical_mode: scannerOriginal.ethical_mode,
      cautious_actives: scannerOriginal.cautious_actives,
    };
    const cOrig: PrefsState = compatOriginal ? {
      allergies: compatOriginal.allergies,
      sensitivity: compatOriginal.sensitivity,
      acne_prone: compatOriginal.acne_prone,
      ethical_mode: compatOriginal.ethical_mode,
      cautious_actives: compatOriginal.cautious_actives,
    } : DEFAULT_PREFS;
    return cmp(scanner, sOrig) || cmp(compat, cOrig);
  }, [scanner, compat, scannerOriginal, compatOriginal]);

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const [s, c] = await Promise.all([
        upsertScannerPrefs(scanner),
        upsertCompatPrefs(compat),
      ]);
      await refreshPrefs();
      const result: PreferencesResult = { scanner: s, compat: c };
      if (mode === 'modal') {
        setSaving(false);
        onComplete?.(result);
      } else {
        setSaving(false);
      }
    } catch (error: unknown) {
      setErr(getErrorMessage(error, 'Failed to save preferences'));
      setSaving(false);
    }
  };

  const inner = (
    <motion.div
      variants={pageIn}
      initial="hidden"
      animate="visible"
      className="mx-auto w-full max-w-2xl"
    >
      {/* Sticky header */}
      <div
        className="sticky top-0 z-10 backdrop-blur px-4 sm:px-0 py-3 border-b"
        style={{ background: 'rgba(248,244,238,0.92)', borderColor: 'var(--rule)' }}
      >
        <div className="row-4 justify-between">
          <h1 className="t-h3" style={{ margin: 0 }}>Preferences</h1>
          <div className="row-3">
            {mode === 'modal' && (
              <button type="button" onClick={() => onCancel?.()} className="btn btn--ghost btn--sm">
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={save}
              disabled={saving || loading || !dirty}
              className="btn btn--primary btn--sm"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        {/* Tab switcher */}
        <div
          className="card card--flat card--bordered"
          style={{ marginTop: 'var(--s-3)', padding: 0, overflow: 'hidden', display: 'inline-grid', gridTemplateColumns: '1fr 1fr' }}
        >
          {(['scanner', 'compat'] as const).map((t, i) => (
            <button
              key={t}
              type="button"
              onClick={() => { setTab(t); }}
              className="t-small transition"
              style={{
                padding: 'var(--s-2) var(--s-5)',
                background: tab === t ? 'var(--c-deep-teal)' : 'transparent',
                color: tab === t ? 'var(--c-cream)' : 'var(--ink-muted)',
                borderRight: i === 0 ? '1px solid var(--rule)' : 'none',
              }}
            >
              {t === 'scanner' ? 'Scanner' : 'Compatibility'}
            </button>
          ))}
        </div>
      </div>

      {/* Context hint */}
      <div style={{ padding: 'var(--s-4) 0', paddingInline: 'var(--s-4)' }} className="sm:px-0">
        <div
          className="card card--flat card--bordered t-small t-mute"
          style={{ padding: 'var(--s-3) var(--s-4)' }}
        >
          {tab === 'scanner'
            ? 'Used by the product scanner and product analysis.'
            : 'Used by the product compatibility checker.'}
        </div>
      </div>

      {/* Form */}
      <div style={{ paddingInline: 'var(--s-4)', paddingBottom: 'var(--s-7)' }} className="sm:px-0">
        {loading ? (
          <p className="t-small t-mute">Loading…</p>
        ) : (
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={tab}
              variants={stagger}
              initial="hidden"
              animate="visible"
            >
              <PrefsForm
                state={tab === 'scanner' ? scanner : compat}
                setState={tab === 'scanner' ? setScanner : setCompat}
                allergens={tab === 'scanner' ? SCANNER_ALLERGENS : COMPAT_ALLERGENS}
                itemVariants={item}
                showSkinType={tab === 'scanner'}
              />
            </motion.div>
          </AnimatePresence>
        )}

        {err && (
          <motion.p
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="pill pill--coral t-small"
            style={{ marginTop: 'var(--s-4)', justifyContent: 'center' }}
          >
            {err}
          </motion.p>
        )}
      </div>
    </motion.div>
  );

  if (mode === 'modal') {
    return (
      <ModalShell title="Preferences" onClose={() => onCancel?.()} widthClass="max-w-2xl">
        <div style={{ padding: 'var(--s-2) var(--s-5) var(--s-2)' }}>{inner}</div>
      </ModalShell>
    );
  }

  return inner;
};

export default PreferencesScreen;
